import { InformationAlertIcon } from "@/assets/images/svgIcons/informationAlertIcon"
import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { BoxOfferValidity, RecurringPeriod } from "@/modules/boxOffer/entities/boxOfferTypes"
import { getRecurringPeriodInputLabel } from "@/modules/boxOffer/utils/boxOfferUtils"
import {
  coachOfferFreePricingFormDefaultValues,
  type CoachOfferPricingFormValues,
  CoachOfferPricingValidationSchema,
  coachOfferSubscriptionPricingFormDefaultValues,
  coachOfferTicketBookPricingFormDefaultValues,
} from "@/modules/coachOffers/types/coachOfferFormTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useUser } from "@/modules/users/hooks/useUser"
import { CoachPaymentOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { BanknoteIcon } from "lucide-react"

type CoachOfferPricingOptionFormProps = {
  onValidSubmit: (values: CoachOfferPricingFormValues) => void
  defaultValues?: CoachOfferPricingFormValues
}

export function CoachOfferPricingOptionForm({ onValidSubmit, defaultValues }: CoachOfferPricingOptionFormProps) {
  const { hideSidebarStackOnTop } = useSidebar()
  const language = useLanguage()
  const user = useUser()

  const form = useAppForm({
    defaultValues: defaultValues ?? coachOfferFreePricingFormDefaultValues,
    validators: {
      onSubmit: CoachOfferPricingValidationSchema(language),
    },
    onSubmit: ({ value }) => {
      onValidSubmit(value)
      hideSidebarStackOnTop()
    },
  })

  const coachOfferItems = [
    { value: CoachPaymentOfferType.Free, label: word("coach.offers.form.pricing.option.free") },
    {
      value: CoachPaymentOfferType.Subscription,
      label: word("coach.offers.form.pricing.option.subscription"),
    },
    {
      value: CoachPaymentOfferType.TicketsBook,
      label: word("coach.offers.form.pricing.option.ticketBook"),
    },
  ]

  const validityItems = [
    {
      value: BoxOfferValidity.UNLIMITED,
      label: word("box.offer.form.infos.validity.types.UNLIMITED"),
    },
    {
      value: BoxOfferValidity.DEFINED,
      label: word("box.offer.form.infos.validity.types.DEFINED"),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-8 px-6 pt-20 pb-7">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <BanknoteIcon color="currentColor" className="size-6" />
          <Text fontStyle="TM">{word("coach.offers.form.pricing.title")}</Text>
        </div>
        <Text fontStyle="BS" className="text-textOnSurface-subtle">
          {word("coach.offers.form.pricing.description")}
        </Text>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto rounded-2xl bg-surface-background-secondary p-4">
        {/* Price type — always visible */}
        <form.AppField name="type" validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.type }}>
          {(field) => (
            <field.Field className="gap-1">
              <field.Label required>
                {word("coach.offers.form.pricing.option.priceType")}              </field.Label>
              <field.Content>
                <field.Select
                  value={field.state.value}
                  onValueChange={(value) => {
                    const newType = value as CoachPaymentOfferType
                    const title = form.getFieldValue("title")
                    const defaultsByType = {
                      [CoachPaymentOfferType.Free]: coachOfferFreePricingFormDefaultValues,
                      [CoachPaymentOfferType.Subscription]: coachOfferSubscriptionPricingFormDefaultValues,
                      [CoachPaymentOfferType.TicketsBook]: coachOfferTicketBookPricingFormDefaultValues,
                    }
                    form.reset({ ...defaultsByType[newType], title })
                  }}
                  items={coachOfferItems}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {coachOfferItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </field.Select>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        {/* Title — always visible */}
        <form.AppField
          name="title"
          validators={{
            onChange: CoachOfferPricingValidationSchema(language).shape.title,
            onBlur: CoachOfferPricingValidationSchema(language).shape.title,
          }}
        >
          {(field) => (
            <field.Field className="gap-1">
              <field.Label required>
                {word("global.title")}              </field.Label>
              <field.Content>
                <field.Input placeholder={word("coach.offers.form.pricing.option.titlePlaceholder")} />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        {/* Subscription-specific fields */}
        <form.Subscribe selector={(state) => state.values.type}>
          {(type) =>
            type === CoachPaymentOfferType.Subscription ? (
              <>
                <form.AppField
                  name="recurringPeriod"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.recurringPeriod }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("coach.offers.form.pricing.option.paymentFrequency")}                      </field.Label>
                      <field.Content>
                        <field.Select
                          value={field.state.value ?? word("box.offer.form.recurring_period.label")}
                          onValueChange={(value) => {
                            field.handleChange(value as RecurringPeriod)
                          }}
                          items={Object.values(RecurringPeriod).map((periodOption) => ({
                            value: periodOption,
                            label: getRecurringPeriodInputLabel(periodOption),
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(RecurringPeriod).map((periodOption) => (
                              <SelectItem key={periodOption} value={periodOption}>
                                {getRecurringPeriodInputLabel(periodOption)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </field.Select>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.AppField
                  name="price"
                  validators={{
                    onChange: CoachOfferPricingValidationSchema(language).shape.price,
                    onBlur: CoachOfferPricingValidationSchema(language).shape.price,
                  }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("global.price.label")}                      </field.Label>
                      <field.Content>
                        <field.PriceInput currency={user.stripeAccountCurrency ?? "EUR"} locale={language} />
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.AppField
                  name="minimumCommitmentPeriod"
                  validators={{
                    onChange: CoachOfferPricingValidationSchema(language).shape.minimumCommitmentPeriod,
                    onBlur: CoachOfferPricingValidationSchema(language).shape.minimumCommitmentPeriod,
                  }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("coach.offers.form.pricing.option.commitmentPeriod")}                      </field.Label>
                      <field.Content>
                        <InputGroup>
                          <InputGroupInput
                            type="number"
                            value={field.state.value ?? ""}
                            onChange={(e) => {
                              field.handleChange(e.target.value)
                            }}
                            onBlur={field.handleBlur}
                            placeholder="1"
                            min={1}
                          />
                          <InputGroupAddon align="inline-end">
                            {word("coach.offers.form.pricing.option.months")}
                          </InputGroupAddon>
                        </InputGroup>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                {/* Info box */}
                <div className="relative flex flex-col gap-4 overflow-hidden rounded-3xl bg-linear-96 from-surface-background-primary-information-secondary to-surface-background-primary-information-default px-6 py-5 before:absolute before:top-0 before:left-0 before:h-full before:w-1.5 before:bg-surface-background-primary-default">
                  <div className="flex flex-row items-center gap-4">
                    <div className="shrink-0">
                      <InformationAlertIcon
                        backgroundColor="var(--surface-background-primary-default)"
                        color="var(--color-interactive-text-primary-default)"
                      />
                    </div>
                    <div className="flex flex-col">
                      <Text fontStyle="TM" className="text-textOnSurface-default">
                        {word("coach.offers.form.pricing.option.commitmentPeriodInfoTitle")}
                      </Text>
                      <Text fontStyle="BM" className="text-textOnSurface-subtle">
                        {word("coach.offers.form.pricing.option.commitmentPeriodInfoDescription")}
                      </Text>
                    </div>
                  </div>
                </div>

                <form.AppField
                  name="renewable"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.renewable }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Content>
                        <field.SwitchInline textSwitch={word("coach.offers.form.pricing.option.autoRenewal")} />
                        <field.Description className="m-0! text-center text-textOnSurface-subtle">
                          {word("coach.offers.form.pricing.option.autoRenewalDescription")}
                        </field.Description>
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              </>
            ) : type === CoachPaymentOfferType.TicketsBook ? (
              <>
                {/* TicketBook fields */}
                <form.AppField
                  name="count"
                  validators={{
                    onChange: CoachOfferPricingValidationSchema(language).shape.count,
                    onBlur: CoachOfferPricingValidationSchema(language).shape.count,
                  }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("coach.offers.form.pricing.option.sessionCount")}                      </field.Label>
                      <field.Content>
                        <InputGroup>
                          <InputGroupInput
                            type="number"
                            value={field.state.value ?? ""}
                            onChange={(e) => {
                              field.handleChange(Number(e.target.value))
                            }}
                            onBlur={field.handleBlur}
                            placeholder="0"
                            min={0}
                          />
                          <InputGroupAddon align="inline-end">
                            {word("coach.offers.form.pricing.option.sessions")}
                          </InputGroupAddon>
                        </InputGroup>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.AppField
                  name="price"
                  validators={{
                    onChange: CoachOfferPricingValidationSchema(language).shape.price,
                    onBlur: CoachOfferPricingValidationSchema(language).shape.price,
                  }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("global.price.label")}                      </field.Label>
                      <field.Content>
                        <field.PriceInput currency={user.stripeAccountCurrency ?? "EUR"} locale={language} />
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.AppField
                  name="validitySwitch"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.validitySwitch }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label className="uppercase">
                        {word("coach.offers.form.pricing.option.validityPeriod")}
                      </field.Label>
                      <field.Content>
                        <field.Select
                          value={field.state.value}
                          onValueChange={(value) => {
                            field.handleChange(value as BoxOfferValidity)
                            if (value === BoxOfferValidity.UNLIMITED) {
                              form.setFieldValue("validityPeriodMonths", 0)
                            } else {
                              form.setFieldValue("validityPeriodMonths", 1)
                            }
                          }}
                          items={validityItems}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {validityItems.map((item) => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </field.Select>
                        <field.Description>
                          {word("coach.offers.form.pricing.option.validityPeriodHelper")}
                        </field.Description>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.Subscribe selector={(state) => state.values.validitySwitch}>
                  {(validitySwitch) =>
                    validitySwitch === BoxOfferValidity.DEFINED ? (
                      <form.AppField
                        name="validityPeriodMonths"
                        validators={{
                          onChange: CoachOfferPricingValidationSchema(language).shape.validityPeriodMonths,
                          onBlur: CoachOfferPricingValidationSchema(language).shape.validityPeriodMonths,
                        }}
                      >
                        {(field) => (
                          <field.Field className="gap-1">
                            <field.Content>
                              <InputGroup>
                                <InputGroupInput
                                  type="number"
                                  value={field.state.value ?? ""}
                                  onChange={(e) => {
                                    field.handleChange(Number(e.target.value))
                                  }}
                                  onBlur={field.handleBlur}
                                  placeholder="1"
                                  min={1}
                                />
                                <InputGroupAddon align="inline-end">
                                  {word("coach.offers.form.pricing.option.months")}
                                </InputGroupAddon>
                              </InputGroup>
                              <field.Message />
                            </field.Content>
                          </field.Field>
                        )}
                      </form.AppField>
                    ) : null
                  }
                </form.Subscribe>

                <form.AppField
                  name="isTrialSession"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.isTrialSession }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Content>
                        <field.SwitchInline textSwitch={word("coach.offers.form.pricing.option.trialSession")} />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              </>
            ) : (
              <>
                {/* Free fields */}
                <form.AppField
                  name="count"
                  validators={{
                    onChange: CoachOfferPricingValidationSchema(language).shape.count,
                    onBlur: CoachOfferPricingValidationSchema(language).shape.count,
                  }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label required>
                        {word("coach.offers.form.pricing.option.sessionCount")}                      </field.Label>
                      <field.Content>
                        <InputGroup>
                          <InputGroupInput
                            type="number"
                            value={field.state.value ?? ""}
                            onChange={(e) => {
                              field.handleChange(Number(e.target.value))
                            }}
                            onBlur={field.handleBlur}
                            placeholder="0"
                            min={0}
                          />
                          <InputGroupAddon align="inline-end">
                            {word("coach.offers.form.pricing.option.sessions")}
                          </InputGroupAddon>
                        </InputGroup>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.AppField
                  name="validitySwitch"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.validitySwitch }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Label className="uppercase">
                        {word("coach.offers.form.pricing.option.validityPeriod")}
                      </field.Label>
                      <field.Content>
                        <field.Select
                          value={field.state.value}
                          onValueChange={(value) => {
                            field.handleChange(value as BoxOfferValidity)
                            if (value === BoxOfferValidity.UNLIMITED) {
                              form.setFieldValue("validityPeriodMonths", 0)
                            } else {
                              form.setFieldValue("validityPeriodMonths", 1)
                            }
                          }}
                          items={validityItems}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {validityItems.map((item) => (
                              <SelectItem key={item.value} value={item.value}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </field.Select>
                        <field.Description>
                          {word("coach.offers.form.pricing.option.validityPeriodHelper")}
                        </field.Description>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>

                <form.Subscribe selector={(state) => state.values.validitySwitch}>
                  {(validitySwitch) =>
                    validitySwitch === BoxOfferValidity.DEFINED ? (
                      <form.AppField
                        name="validityPeriodMonths"
                        validators={{
                          onChange: CoachOfferPricingValidationSchema(language).shape.validityPeriodMonths,
                          onBlur: CoachOfferPricingValidationSchema(language).shape.validityPeriodMonths,
                        }}
                      >
                        {(field) => (
                          <field.Field className="gap-1">
                            <field.Content>
                              <InputGroup>
                                <InputGroupInput
                                  type="number"
                                  value={field.state.value ?? ""}
                                  onChange={(e) => {
                                    field.handleChange(Number(e.target.value))
                                  }}
                                  onBlur={field.handleBlur}
                                  placeholder="1"
                                  min={1}
                                />
                                <InputGroupAddon align="inline-end">
                                  {word("coach.offers.form.pricing.option.months")}
                                </InputGroupAddon>
                              </InputGroup>
                              <field.Message />
                            </field.Content>
                          </field.Field>
                        )}
                      </form.AppField>
                    ) : null
                  }
                </form.Subscribe>

                <form.AppField
                  name="isTrialSession"
                  validators={{ onChange: CoachOfferPricingValidationSchema(language).shape.isTrialSession }}
                >
                  {(field) => (
                    <field.Field className="gap-1">
                      <field.Content>
                        <field.SwitchInline textSwitch={word("coach.offers.form.pricing.option.trialSession")} />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              </>
            )
          }
        </form.Subscribe>
      </div>

      <Button
        variant="primary"
        size="lg"
        className="w-full"
        onClick={() => {
          void form.handleSubmit()
        }}
      >
        {word("global.check")}
      </Button>
    </div>
  )
}
